import React, { useState, useEffect } from 'react';
import {
    Box, Flex, Heading, Text, Badge, Button, Grid, GridItem,
    Spinner, useColorModeValue, useToast,
    Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalBody, ModalFooter, ModalCloseButton, useDisclosure,
    HStack, Tag, TagLabel, TagLeftIcon, Tooltip,
} from '@chakra-ui/react';
import {
    MdAdd, MdCheckCircle, MdCancel, MdLink, MdOpenInNew,
    MdRefresh, MdApi,
} from 'react-icons/md';
import { SiGooglesheets } from 'react-icons/si';
import { FaNetworkWired } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosClient from '../../../../axios-client';
import { useStateContext } from '../../../../context/ContextProvider';

// ─── Icon / color maps ────────────────────────────────────────────────────────
const PLATFORM_ICONS = {
    google_sheets: <SiGooglesheets size={36} color="#34A853" />,
    codnetwork: <FaNetworkWired size={36} color="#3182ce" />,
};

const PLATFORM_COLORS = {
    google_sheets: 'green',
    codnetwork: 'blue',
};

export default function Gestion_Webhook_Modify() {
    const { t } = useTranslation();
    const { user, currentBusiness } = useStateContext();
    const toast = useToast();
    const navigate = useNavigate();

    const [platforms, setPlatforms] = useState([]);
    const [configurations, setConfigurations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [connecting, setConnecting] = useState(null);       // platform.code being connected
    const [disconnecting, setDisconnecting] = useState(null); // conn.id being deleted
    const [toggling, setToggling] = useState(null);           // conn.id being toggled

    // Disconnect confirm modal
    const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
    const [pendingPlatform, setPendingPlatform] = useState(null);

    // Theme colors (identical pattern to MyAPIs)
    const cardBg      = useColorModeValue('white', 'gray.700');
    const borderC     = useColorModeValue('gray.200', 'gray.600');
    const subText     = useColorModeValue('gray.500', 'gray.400');
    const hubBg       = useColorModeValue('gray.50', 'gray.800');
    const connectedBg = useColorModeValue('purple.50', 'purple.900');

    // ── Fetch ──────────────────────────────────────────────────────────────
    const fetchAll = async () => {
        if (!user?.id) return;
        setLoading(true);
        try {
            const [platRes, connRes] = await Promise.all([
                axiosClient.get('/api/webhook-modify/platforms/'),
                axiosClient.get(`/api/webhook-modify/list/${user.id}/`),
            ]);
            if (platRes.data.success) setPlatforms(platRes.data.platforms);
            if (connRes.data.success) {
                // Filter by current business
                const filtered = connRes.data.configurations.filter(
                    c => c.business_id === (currentBusiness?.id || null)
                );
                setConfigurations(filtered);
            }
        } catch {
            toast({ description: t('Erreur lors du chargement.'), status: 'error', duration: 3000, isClosable: true, position: 'bottom-right' });
        }
        setLoading(false);
    };

    useEffect(() => { fetchAll(); }, [user?.id, currentBusiness?.id]);

    // ── Helpers ────────────────────────────────────────────────────────────
    const getConnection = (platformCode) =>
        configurations.find(c => c.platform === platformCode) || null;

    const activeCount = configurations.filter(c => c.is_active).length;

    // ── Redirect after connect ─────────────────────────────────────────────
    const redirectToPlatform = (platformCode, config) => {
        // All platforms use the same unified doc page — content adapts via config.platform
        navigate('/modifier-commandes-webhook/documentation', { state: { config } });
    };

    // ── Connect ────────────────────────────────────────────────────────────
    const handleConnect = async (platform) => {
        const existingConn = getConnection(platform.code);
        if (existingConn) {
            // Already connected — go straight to docs/guide
            redirectToPlatform(platform.code, existingConn);
            return;
        }
        setConnecting(platform.code);
        try {
            const payload = {
                platform: platform.code,
                name: platform.name,
                business_id: currentBusiness?.id || null,
            };
            if (platform.code === 'google_sheets') {
                payload.sheet_name = 'Modification Commandes';
                payload.sheet_url = '';
            }
            const res = await axiosClient.post(`/api/webhook-modify/create/${user.id}/`, payload);
            if (res.data.success) {
                toast({ description: t('Connexion créée avec succès !'), status: 'success', duration: 3000, isClosable: true, position: 'bottom-right' });
                await fetchAll();
                redirectToPlatform(platform.code, res.data.configuration);
            } else {
                toast({ description: res.data.error || t('Erreur lors de la connexion.'), status: 'error', duration: 4000, isClosable: true, position: 'bottom-right' });
            }
        } catch {
            toast({ description: t('Erreur lors de la connexion.'), status: 'error', duration: 4000, isClosable: true, position: 'bottom-right' });
        }
        setConnecting(null);
    };

    // ── Disconnect ─────────────────────────────────────────────────────────
    const handleDisconnect = async () => {
        if (!pendingPlatform) return;
        const conn = getConnection(pendingPlatform.code);
        if (!conn) return;
        setDisconnecting(conn.id);
        try {
            await axiosClient.delete(`/api/webhook-modify/${conn.id}/delete/`);
            toast({ description: t(`Connexion à ${pendingPlatform.name} supprimée.`), status: 'info', duration: 3000, isClosable: true, position: 'bottom-right' });
            fetchAll();
        } catch {
            toast({ description: t('Erreur lors de la déconnexion.'), status: 'error', duration: 3000, isClosable: true, position: 'bottom-right' });
        }
        setDisconnecting(null);
        onConfirmClose();
        setPendingPlatform(null);
    };

    // ── Toggle active ──────────────────────────────────────────────────────
    const handleToggle = async (conn) => {
        setToggling(conn.id);
        try {
            await axiosClient.post(`/api/webhook-modify/${conn.id}/toggle-active/`);
            fetchAll();
        } catch (err) {
            toast({ description: err?.response?.data?.error || t('Erreur.'), status: 'error', duration: 3000, isClosable: true, position: 'bottom-right' });
        }
        setToggling(null);
    };

    // ── Render ─────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <Flex justify="center" align="center" minH="300px">
                <Spinner size="xl" color="purple.500" />
            </Flex>
        );
    }

    return (
        <Box bg={hubBg} minH="100vh" p={{ base: 4, md: 8 }}>

            {/* ── Header ─────────────────────────────────────────────────── */}
            <Flex
                justify="space-between"
                align={{ base: 'flex-start', md: 'center' }}
                direction={{ base: 'column', md: 'row' }}
                mb={8}
                gap={4}
            >
                <Box>
                    <Heading size="lg" mb={1}>🔗 Modifier via Webhook</Heading>
                    <Text color={subText} fontSize="sm">
                        Connectez vos plateformes externes pour modifier automatiquement les statuts de vos commandes dans Ecolinke.
                    </Text>
                </Box>
                <HStack>
                    <Tag colorScheme="purple" size="lg" rounded="full" px={4}>
                        <TagLeftIcon as={MdCheckCircle} />
                        <TagLabel>{activeCount} {t('connectée(s)')}</TagLabel>
                    </Tag>
                    <Button size="sm" leftIcon={<MdRefresh />} onClick={fetchAll} variant="outline">
                        {t('Actualiser')}
                    </Button>
                </HStack>
            </Flex>

            {/* ── Cards Grid ─────────────────────────────────────────────── */}
            <Grid
                templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
                gap={6}
            >
                {platforms.map((platform) => {
                    const conn = getConnection(platform.code);
                    const isConnected = !!conn;
                    const isActive = conn?.is_active;
                    const colorScheme = PLATFORM_COLORS[platform.code] || 'purple';

                    return (
                        <GridItem key={platform.id}>
                            <Box
                                bg={isConnected ? connectedBg : cardBg}
                                border="2px solid"
                                borderColor={isConnected ? `${colorScheme}.300` : borderC}
                                rounded="2xl"
                                p={6}
                                shadow={isConnected ? 'md' : 'sm'}
                                transition="all 0.2s"
                                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                                position="relative"
                            >
                                {/* Status badge */}
                                {isConnected && (
                                    <Badge
                                        position="absolute"
                                        top={3}
                                        right={3}
                                        colorScheme={isActive ? 'green' : 'red'}
                                        rounded="full"
                                        px={2}
                                    >
                                        {isActive ? t('Actif') : t('Inactif')}
                                    </Badge>
                                )}

                                {/* Icon + Name */}
                                <Flex align="center" gap={3} mb={4}>
                                    <Box>{PLATFORM_ICONS[platform.code] || <MdApi size={36} color="#805AD5" />}</Box>
                                    <Box>
                                        <Text fontWeight="bold" fontSize="lg">{platform.name}</Text>
                                        <Badge colorScheme={colorScheme} fontSize="xs">{platform.code}</Badge>
                                    </Box>
                                </Flex>

                                {/* Description */}
                                <Text fontSize="sm" color={subText} mb={5} noOfLines={3}>
                                    {platform.description || 'Modifier vos commandes automatiquement via Webhook.'}
                                </Text>

                                {/* Stats row (if connected) — mirrors MyAPIs */}
                                {isConnected && (
                                    <Flex gap={4} mb={4} fontSize="xs" color={subText}>
                                        <Box>
                                            <Text fontWeight="semibold">{conn.total_orders_modified ?? 0}</Text>
                                            <Text>{t('commandes modifiées')}</Text>
                                        </Box>
                                        <Box>
                                            <Text fontWeight="semibold">
                                                {conn.last_modify
                                                    ? new Date(conn.last_modify).toLocaleDateString('fr-FR')
                                                    : '-'}
                                            </Text>
                                            <Text>{t('dernière modification')}</Text>
                                        </Box>
                                    </Flex>
                                )}

                                {/* Actions — exactly mirrors MyAPIs pattern */}
                                <Flex gap={2} flexWrap="wrap">
                                    {!isConnected ? (
                                        <Button
                                            size="sm"
                                            colorScheme={colorScheme}
                                            leftIcon={<MdAdd />}
                                            isLoading={connecting === platform.code}
                                            onClick={() => handleConnect(platform)}
                                            w="full"
                                        >
                                            {t('Connecter')}
                                        </Button>
                                    ) : (
                                        <>
                                            {/* Voir documentation / guide */}
                                            <Button
                                                size="sm"
                                                colorScheme={colorScheme}
                                                leftIcon={<MdOpenInNew />}
                                                onClick={() => redirectToPlatform(platform.code, conn)}
                                                flex={1}
                                            >
                                                {t('Voir détails')}
                                            </Button>

                                            {/* Toggle active / inactive */}
                                            <Tooltip label={isActive ? t('Désactiver') : t('Activer')}>
                                                <Button
                                                    size="sm"
                                                    colorScheme={isActive ? 'yellow' : 'green'}
                                                    leftIcon={isActive ? <MdCancel /> : <MdCheckCircle />}
                                                    isLoading={toggling === conn.id}
                                                    onClick={() => handleToggle(conn)}
                                                >
                                                    {isActive ? t('Désactiver') : t('Activer')}
                                                </Button>
                                            </Tooltip>

                                            {/* Disconnect */}
                                            <Tooltip label={t('Déconnecter')}>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    colorScheme="red"
                                                    isLoading={disconnecting === conn.id}
                                                    onClick={() => {
                                                        setPendingPlatform(platform);
                                                        onConfirmOpen();
                                                    }}
                                                >
                                                    <MdLink />
                                                </Button>
                                            </Tooltip>
                                        </>
                                    )}
                                </Flex>
                            </Box>
                        </GridItem>
                    );
                })}
            </Grid>


            <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{t('Confirmer la déconnexion')}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text>
                            {t('Êtes-vous sûr de vouloir supprimer la connexion à')}{' '}
                            <strong>{pendingPlatform?.name}</strong> ?{' '}
                            {t('Toutes les configurations et le token associé seront perdus.')}
                        </Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button colorScheme="red" isLoading={!!disconnecting} onClick={handleDisconnect}>
                            {t('Supprimer')}
                        </Button>
                        <Button variant="ghost" onClick={onConfirmClose}>{t('Annuler')}</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
